import React, { useState, useEffect } from 'react'
import { X, ThumbsUp, ThumbsDown, Loader2, Check } from 'lucide-react'
import { feedbackService } from '../../services/feedbackService'
import captions from '../../data/captions.json'

interface FeedbackModalProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * Feedback Modal - Clean, principle-driven design
 * Following Design Principles:
 * - Visual Hierarchy: Clear structure, size/weight/contrast
 * - Readability-First: Full opacity text, high contrast
 * - White Space: 24px padding, 16px gaps
 * - Affordance: Clear states, hover feedback
 * - Feedback Loop: Loading states, success confirmation
 */
const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const [rating, setRating] = useState<'positive' | 'negative' | null>(null)
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setRating(null)
      setMessage('')
      setIsSubmitted(false)
    }
  }, [isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!message.trim()) return

    setIsSubmitting(true)

    try {
      // Capture context
      const pageUrl = window.location.href

      // Try to extract language context from URL or page
      let sourceLanguage: string | undefined
      let targetLanguage: string | undefined

      // Check if we're on a creator page with language selectors
      const sourceEl = document.querySelector('[data-source-language]')
      const targetEl = document.querySelector('[data-target-language]')
      if (sourceEl) sourceLanguage = sourceEl.getAttribute('data-source-language') || undefined
      if (targetEl) targetLanguage = targetEl.getAttribute('data-target-language') || undefined

      await feedbackService.submitFeedback({
        rating: rating || undefined,
        message: message.trim(),
        pageUrl,
        sourceLanguage,
        targetLanguage
      })

      setIsSubmitted(true)

      // Auto-close after 2 seconds
      setTimeout(() => {
        onClose()
      }, 2000)

    } catch (error) {
      console.error('Failed to submit feedback:', error)
      alert('Failed to submit feedback. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop (Principle: Visual Hierarchy - modal on top) */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* Modal (Principles: White Space, Readability, Contrast) */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden pointer-events-auto animate-in fade-in zoom-in duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {isSubmitted ? (
            /* Success State (Principle: Feedback Loop) */
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Thank you!</h3>
              <p className="text-gray-600">Your feedback helps us improve SyntaxLens.</p>
            </div>
          ) : (
            <>
              {/* Header (Principle: Visual Hierarchy) */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Send Feedback</h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded hover:bg-gray-100"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Form (Principles: Readability, White Space) */}
              <form onSubmit={handleSubmit} className="p-6">
                {/* Optional Rating (Principle: Progressive Disclosure) */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {captions.feedback.prompt}
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setRating('positive')}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                        rating === 'positive'
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                    >
                      <ThumbsUp className="w-5 h-5" />
                      <span className="font-medium">Good</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setRating('negative')}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                        rating === 'negative'
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                    >
                      <ThumbsDown className="w-5 h-5" />
                      <span className="font-medium">Needs work</span>
                    </button>
                  </div>
                </div>

                {/* Message (Principle: Readability-First) */}
                <div className="mb-6">
                  <label htmlFor="feedback-message" className="block text-sm font-medium text-gray-700 mb-2">
                    Your feedback
                  </label>
                  <textarea
                    id="feedback-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Tell us what you think, what could be improved, or report a bug..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none text-gray-900 placeholder-gray-400"
                    rows={4}
                    required
                  />
                </div>

                {/* Submit (Principle: Affordance, Contrast) */}
                <button
                  type="submit"
                  disabled={!message.trim() || isSubmitting}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors duration-200"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <span>Send Feedback</span>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default FeedbackModal
